import {
  Bell,
  Moon,
  RefreshCw,
  Sun,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { Avatar } from "@/components/catalyst/avatar";
import { NavbarDivider } from "@/components/catalyst/navbar";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "@/components/catalyst/dropdown";
import { useAuthStore } from "@/store/auth";
import { useThemeStore } from "@/store/theme";
import { useTrackingStore } from "@/store/tracking";
import { useDashboardRefreshStore } from "@/store/dashboardRefresh";

const pageTitles: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/analytics": "Analytics",
  "/reports": "Reports",
  "/alerts": "Alerts",
  "/devices": "Devices",
  "/tracking": "Track Your Device",
  "/profile": "Profile",
  "/admin": "Settings",
  "/help": "Help & Support",
};

function currentTitle(pathname: string) {
  const match = Object.keys(pageTitles).find((path) => pathname.startsWith(path));
  return match ? pageTitles[match] : "XapSync";
}

export function PageHeader() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const trackingDevice = useTrackingStore(
    (state) => state.selectedDevice,
  );
  const isTracking = location.pathname.startsWith("/tracking");
  const isDashboard = location.pathname.startsWith("/dashboard");
  const dashboardRefresh = useDashboardRefreshStore();

  // Navigation now lives in the sidebar, so the top bar is
  // only rendered on the Dashboard.
  if (!isDashboard) return null;

  const initials = user
    ? `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase() ||
      user.email[0]?.toUpperCase()
    : "?";

  return (
    <div className="mb-6 hidden min-w-0 items-center gap-2 border-b
      lg:flex
      border-border pb-4 sm:gap-3 lg:gap-4"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div
            className="flex min-w-0 items-center gap-2.5"
          >
            <h1 className="shrink-0 text-lg font-semibold">
              {currentTitle(location.pathname)}
            </h1>

            {isDashboard && dashboardRefresh.refresh && (
              <button
                type="button"
                disabled={dashboardRefresh.isRefreshing}
                onClick={dashboardRefresh.refresh}
                title={
                  dashboardRefresh.updatedAt
                    ? `Updated ${
                        new Date(
                          dashboardRefresh.updatedAt,
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      }. Click to refresh now.`
                    : "Click to refresh now."
                }
                aria-label={
                  dashboardRefresh.isRefreshing
                    ? "Refreshing dashboard"
                    : `Refresh dashboard. Next refresh in ${
                        dashboardRefresh.refreshIn
                      } seconds.`
                }
                className="inline-flex h-7 w-[62px] shrink-0
                  items-center justify-center gap-1.5 rounded-md
                  border border-slate-200 bg-transparent px-2
                  text-[11px] font-semibold tabular-nums
                  text-slate-600 transition-colors duration-150
                  hover:border-blue-200 hover:bg-blue-50
                  hover:text-blue-700 focus:outline-none
                  focus-visible:ring-2 focus-visible:ring-blue-500/25
                  disabled:cursor-wait disabled:opacity-70
                  dark:border-white/10 dark:text-slate-300
                  dark:hover:border-blue-400/20
                  dark:hover:bg-blue-400/10
                  dark:hover:text-blue-200"
              >
                <RefreshCw
                  className={
                    dashboardRefresh.isRefreshing
                      ? "size-3.5 animate-spin"
                      : "size-3.5"
                  }
                  aria-hidden="true"
                />

                <span>
                  {dashboardRefresh.isRefreshing
                    ? "Sync"
                    : `${dashboardRefresh.refreshIn}s`}
                </span>
              </button>
            )}
          </div>
          {isTracking && trackingDevice && (
            <span className="flex min-w-0 items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-800 dark:bg-blue-950/60 dark:text-blue-100">
              <span className={`size-2 shrink-0 rounded-full ${
                trackingDevice.status === "online"
                  ? "bg-emerald-500"
                  : "bg-slate-400"
              }`} />
              <span className="max-w-44 truncate font-medium">
                {trackingDevice.name}
              </span>
              <span className="hidden text-blue-500 sm:inline dark:text-blue-300">
                {trackingDevice.serial_number}
              </span>
              {trackingDevice.hasGeofence && (
                <span className="font-medium text-violet-700 dark:text-violet-300">
                  Geofence
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      <div className="ml-auto" />

      <button
        type="button"
        aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        onClick={toggleTheme}
        className="shrink-0 rounded-lg p-2 text-muted-foreground
          hover:bg-muted hover:text-foreground"
      >
        {theme === "dark" ? <Sun className="size-5" /> : <Moon className="size-5" />}
      </button>

      <button
        type="button"
        aria-label="Notifications"
        className="relative shrink-0 rounded-lg p-2
          text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <Bell className="size-5" />
        <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-critical" />
      </button>

      <NavbarDivider className="hidden h-6 sm:block" />

      <Dropdown>
        <DropdownButton
          as="button"
          className="flex items-center gap-2 rounded-lg p-1 hover:bg-muted"
        >
          <Avatar initials={initials} className="size-8 bg-primary text-primary-foreground" />
        </DropdownButton>
        <DropdownMenu anchor="bottom end">
          <DropdownItem href="/profile">
            <DropdownLabel>Profile</DropdownLabel>
          </DropdownItem>
          <DropdownDivider />
          <DropdownItem onClick={logout}>
            <DropdownLabel>Sign out</DropdownLabel>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

export function MobilePageHeader() {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const initials = user
    ? `${user.first_name?.[0] ?? ""}${
        user.last_name?.[0] ?? ""
      }`.toUpperCase() || user.email[0]?.toUpperCase()
    : "?";

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <h1 className="min-w-0 flex-1 truncate text-base font-semibold">
        {currentTitle(location.pathname)}
      </h1>
      <Dropdown>
        <DropdownButton
          as="button"
          aria-label="Open profile menu"
          className="flex cursor-pointer items-center rounded-lg p-1
            hover:bg-muted"
        >
          <Avatar
            initials={initials}
            className="size-8 bg-primary text-primary-foreground"
          />
        </DropdownButton>
        <DropdownMenu anchor="bottom end">
          <DropdownItem href="/profile">
            <DropdownLabel>Profile</DropdownLabel>
          </DropdownItem>
          <DropdownDivider />
          <DropdownItem onClick={logout}>
            <DropdownLabel>Sign out</DropdownLabel>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}
